/**
 * CovenantExplanationCard — renders the get_covenant_explanation (T-27) tool
 * result: the authored explanation for a covenant type joined with the user's
 * OWN extracted threshold and, for leverage_ratio / interest_coverage_ratio,
 * a NEUTRAL positional percentile bar against the matched industry cohort.
 *
 * Hard rules (mirror ComponentDetailCard): semantic tokens only, zero rounded
 * corners, IBM Plex Mono + tabular-nums for numbers, sentence case in chrome,
 * no exclamation marks / em dashes in the card's own copy (authored covenant
 * content is rendered verbatim), no letter grades, no forward cost estimates.
 *
 * Fix 4 (positional, never evaluative): the PositionBar is a single neutral
 * design-system fill, NOT a green->red ramp. Fix 3 (severity-band coherence):
 * the card shows no colour-coded verdict at all, so it cannot contradict the
 * covenant-severity engine — one coherent, neutral, positional signal.
 */
import { Shield } from 'react-feather';

import type { ToolResultProps } from '../lib/component-registry';

interface CovenantContentBlock {
  explanation?: string | null;
  impact?: string | null;
  considerations?: string[] | null;
  typical_threshold_range?: string | null;
  headroom_structure?: string | null;
  breach_consequence?: string | null;
}

interface OwnThreshold {
  threshold?: number | null;
  threshold_text?: string | null;
  testing_frequency?: string | null;
  direction?: 'higher_better' | 'lower_better' | null;
}

interface CovenantExplanationResult {
  is_error?: boolean;
  covenant_type?: string;
  covenant_label?: string;
  term_sheet_name?: string | null;
  content_available?: boolean;
  content?: CovenantContentBlock | null;
  own_threshold_available?: boolean;
  own?: OwnThreshold | null;
  percentile_available?: boolean;
  percentile_rank?: number | null;
  cohort_p10?: number | null;
  cohort_median?: number | null;
  cohort_p90?: number | null;
  cohort_sample_size?: number | null;
  cohort_industry?: string | null;
}

function capFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Ratio covenants (a numeric direction is set) render with an 'x' multiple
 *  suffix; anything else shows the bare number and leans on the verbatim clause. */
function formatThreshold(value: number, direction: OwnThreshold['direction']): string {
  return direction ? `${value}x` : `${value}`;
}

/** Neutral, positional fill (design-system orange) — never an evaluative ramp. */
function PositionBar({ percentile }: { percentile: number }) {
  const p = Math.max(0, Math.min(100, percentile));
  return (
    <span className="relative inline-block h-1 w-16 bg-surface-subtle align-middle">
      <span className="absolute inset-y-0 left-0 bg-orange" style={{ width: `${p}%` }} />
    </span>
  );
}

function LabeledParagraph({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">{label}</div>
      <p className="mt-0.5 text-body-sm text-txt-secondary">{text}</p>
    </div>
  );
}

export function CovenantExplanationCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as Partial<CovenantExplanationResult>;

  // Unknown / absent covenant type -> no card, the prose carries (edge-case gate).
  if (r.is_error || !r.content_available || !r.content) {
    return null;
  }

  const content = r.content;
  const label = r.covenant_label ?? (r.covenant_type ? capFirst(r.covenant_type.replace(/_/g, ' ')) : 'Covenant');
  const own = r.own ?? null;
  const hasOwn = r.own_threshold_available === true && own !== null;
  const hasNumber = hasOwn && typeof own?.threshold === 'number';
  const hasPercentile =
    r.percentile_available === true && typeof r.percentile_rank === 'number';

  return (
    <div className="animate-card-enter mt-2 border border-border bg-surface-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-orange">
            <Shield size={16} />
          </span>
          <p className="text-caption font-semibold uppercase tracking-wide text-txt-secondary">{label}</p>
        </div>
        {r.term_sheet_name ? (
          <span className="truncate pl-2 text-caption text-txt-tertiary">{r.term_sheet_name}</span>
        ) : null}
      </div>

      <div className="px-4 py-3">
        {/* Explanation + typical range */}
        {content.explanation ? (
          <p className="text-body-sm text-txt-primary">{content.explanation}</p>
        ) : null}
        {content.typical_threshold_range ? (
          <div className="mt-3 bg-surface-subtle px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">Typical range</div>
            <p className="mt-0.5 text-body-sm text-txt-secondary">{content.typical_threshold_range}</p>
          </div>
        ) : null}

        {/* Your threshold */}
        <div className="mt-3 flex items-start justify-between border-b border-border py-2">
          <span className="text-body-sm text-txt-secondary">Your threshold</span>
          {hasOwn ? (
            <span className="flex flex-col items-end gap-0.5 pl-3 text-right">
              {hasNumber ? (
                <span className="font-mono text-body-sm tabular-nums text-txt-primary">
                  {formatThreshold(own!.threshold as number, own?.direction)}
                </span>
              ) : null}
              {own?.threshold_text ? (
                <span className="text-caption text-txt-secondary">{own.threshold_text}</span>
              ) : null}
              {own?.testing_frequency ? (
                <span className="mt-0.5 border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-txt-tertiary">
                  {capFirst(own.testing_frequency)}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="pl-3 text-body-sm italic text-txt-tertiary">Not detected in this term sheet</span>
          )}
        </div>

        {/* Cohort position — leverage_ratio / ICR only, neutral + positional */}
        {hasPercentile ? (
          <div className="border-b border-border py-2">
            <div className="flex items-center justify-between">
              <span className="text-body-sm text-txt-secondary">
                vs {r.cohort_industry ?? 'industry'} cohort
                {typeof r.cohort_sample_size === 'number' ? ` (n=${r.cohort_sample_size})` : ''}
              </span>
              <span className="flex items-center gap-2">
                <PositionBar percentile={r.percentile_rank as number} />
                <span className="font-mono text-body-sm tabular-nums text-txt-primary">
                  p{Math.round(r.percentile_rank as number)}
                </span>
              </span>
            </div>
            {typeof r.cohort_p10 === 'number' &&
            typeof r.cohort_median === 'number' &&
            typeof r.cohort_p90 === 'number' ? (
              <div className="mt-1 text-[10px] tabular-nums text-txt-tertiary">
                cohort p10 {r.cohort_p10}x · median {r.cohort_median}x · p90 {r.cohort_p90}x
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Headroom + breach */}
        {content.headroom_structure ? (
          <LabeledParagraph label="How headroom works" text={content.headroom_structure} />
        ) : null}
        {content.breach_consequence ? (
          <LabeledParagraph label="If breached" text={content.breach_consequence} />
        ) : null}
      </div>
    </div>
  );
}
